/* eslint-disable @typescript-eslint/no-unused-vars */

import * as React from 'react';
import Button from 'reactstrap/lib/Button';
import { getStrongholdPayClient } from '../../config';
import { fetchCustomerTokenAction, setActionRequestingAction, removePaymentSourceAction } from '../../store/actions';
import { connect, MapStateToPropsParam } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { ChargeOnSuccess, ERROR_CODE, OnError, OnExit } from '@stronghold/pay-dropin';
import { ApplicationState } from '../../store';
import selectors from '../../store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from 'reactstrap/lib/Spinner';
import { ButtonProps } from 'reactstrap';
import { faMoneyBill } from '@fortawesome/free-solid-svg-icons/faMoneyBill';
import { ApiKeyModel } from '../../apis';

interface StateProps {
    publishableKey: ApiKeyModel | null;
    customerToken: string;
    noSource: boolean;
    isAuthorizingPayment: boolean;
    integrationId?: string;
    apiVersion: string;
    authorizeOnly?: boolean;
}

interface OwnProps extends ButtonProps {
    payLinkCode: string;
    paymentSourceId: string;
    amount: number;
    tipAmount?: number;
    tipBeneficiaryName?: string;
    onSuccess: ChargeOnSuccess;
    creditAmount?: number;
    processingFee?: number;
}

interface DispatchProps {
    fetchCustomerTokenAction: typeof fetchCustomerTokenAction;
    setActionRequestingAction: typeof setActionRequestingAction;
    removePaymentSourceAction: typeof removePaymentSourceAction;
}

type Props = StateProps & OwnProps & DispatchProps;

interface State {
    ready: boolean;
}

class AddChargeDropinButton extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = { ready: true };
    }

    charge = async () => {
        // Return if no key
        if (!this.props.publishableKey) return;

        this.props.setActionRequestingAction('authorize_payment', true);
        try {
            await this.props.fetchCustomerTokenAction();
        } catch (e) {
            this.props.setActionRequestingAction('authorize_payment', false);
            // Something wrong happen, possibly display an error in the future
            return;
        }

        // If for whatever reason there is no customer token, throw
        if (!this.props.customerToken) throw new Error('no customer token');

        const client = getStrongholdPayClient(
            this.props.publishableKey.key,
            this.props.publishableKey.environment,
            this.props.apiVersion,
            this.props.integrationId,
        );
        client.charge(this.props.customerToken, {
            payLinkCode: this.props.payLinkCode,
            skipSuccessScreen: true,
            skipPayScreen: true,
            authorizeOnly: !!this.props.authorizeOnly,
            charge: {
                amount: this.props.amount,
                paymentSourceId: this.props.paymentSourceId,
            },
            tip: this.props.tipAmount
                ? {
                      amount: this.props.tipAmount,
                      beneficiaryName: this.props.tipBeneficiaryName || '',
                  }
                : undefined,
            onSuccess: this.onSuccess,
            onExit: this.onExit,
            onError: this.onError,
        });
    };

    updatePaymentMethod = async () => {
        // Return if no key
        if (!this.props.publishableKey) return;

        this.setState({ ready: false });

        const client = getStrongholdPayClient(
            this.props.publishableKey.key,
            this.props.publishableKey.environment,
            this.props.apiVersion,
            this.props.integrationId,
        );
        client.updatePaymentSource(this.props.customerToken, {
            onSuccess: () => this.setState({ ready: true }),
            onExit: () => this.setState({ ready: true }),
            onError: (e) => {
                this.setState({ ready: true });
            },
            paymentSourceId: this.props.paymentSourceId,
        });
    };

    onSuccess: ChargeOnSuccess = (charge, correlation) => {
        this.props.setActionRequestingAction('authorize_payment', false);
        if (this.props.onSuccess) {
            this.props.onSuccess(charge, correlation);
        }
    };

    onError: OnError = async (e) => {
        if (e.code === ERROR_CODE.PAYMENT_SOURCE_LOGIN_REQUIRED) {
            this.setState({ ready: false });
            // TODO: see how to fix latency of event listener
            setTimeout(() => this.updatePaymentMethod(), 200);
            return;
        } else if (e.code === ERROR_CODE.PAYMENT_SOURCE_INACTIVE) {
            this.setState({ ready: false });
            this.props.removePaymentSourceAction(this.props.paymentSourceId);
            return;
        }
        this.props.setActionRequestingAction('authorize_payment', false);
    };

    onExit: OnExit = () => {
        this.props.setActionRequestingAction('authorize_payment', false);
    };

    render() {
        const payAmount =
            this.props.amount +
            (this.props.tipAmount || 0) -
            (this.props.creditAmount || 0) +
            (this.props.processingFee || 0);
        const {
            children = <>PAY {selectors.number.amountToString(payAmount > 0 ? payAmount : 0)}</>,
            payLinkCode,
            isAuthorizingPayment,
            customerToken,
            paymentSourceId,
            onSuccess,
            onExit,
            fetchCustomerTokenAction,
            noSource,
            setActionRequestingAction,
            publishableKey,
            merchantDisplayName,
            paymentSourceLabel,
            amount,
            tipAmount = 0,
            tipBeneficiaryName,
            subButtonElement,
            ...props
        } = this.props;
        const { ready } = this.state;

        let icon = <FontAwesomeIcon icon={faMoneyBill} fixedWidth />;

        const isLoading = isAuthorizingPayment;
        const isDisabled = isLoading || noSource || props.disabled || !publishableKey || !ready || !amount;

        if (isLoading) {
            icon = <Spinner type="grow" color="white" size="sm" />;
        }

        return (
            <Button color="primary" {...props} onClick={this.charge} data-sh="charge-button" disabled={isDisabled}>
                {children}
                <span className="ml-2">{icon}</span>
            </Button>
        );
    }
}

const mapStateToProps: MapStateToPropsParam<StateProps, OwnProps, ApplicationState> = (state) => ({
    publishableKey: state.global.publishableApiKey,
    authorizeOnly: state.payLink.data?.authorize_only,
    customerToken: state.authentication.customerToken || '',
    noSource: state.paymentSources.arr.length === 0,
    isAuthorizingPayment: selectors.global.isActionRequesting(state.global.actions, 'authorize_payment'),
    integrationId: state.attribution?.integrationId,
    apiVersion: state.attribution.apiVersion,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            fetchCustomerTokenAction,
            setActionRequestingAction,
            removePaymentSourceAction,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(AddChargeDropinButton);
